/**
 * ════════════════════════════════════════════════════════════
 *  ECHO CONFIGURATION TYPES
 *  Complete TypeScript types for Organization.echoConfig
 * ════════════════════════════════════════════════════════════
 */

// ═════════════════════════════════════════════════════════════
//  EVENT FILTERING
// ═════════════════════════════════════════════════════════════

export interface EchoEventFiltering {
  /** Whitelist of enabled events (wildcards supported: "user.*", "*") */
  enabled: string[];

  /** Blacklist of disabled events (overrides enabled) */
  disabled: string[];

  /** Sampling rates per event type (0.0 to 1.0) */
  sampling: Record<string, number>;
}

// ═════════════════════════════════════════════════════════════
//  RETENTION & ARCHIVAL
// ═════════════════════════════════════════════════════════════

export type EchoStorageType = "s3" | "gcs" | "azure";

export interface EchoArchiveDestination {
  type: EchoStorageType;
  bucket: string;
  path: string;
  region?: string;
  credentials?: {
    accessKeyId?: string;
    secretAccessKey?: string;
  };
}

export type EchoDeletionPolicy = "never" | "after_archive" | "after_retention";

export interface EchoRetention {
  /** Days to keep logs in PostgreSQL database */
  daysInDatabase: number;

  /** Auto-archive logs to cold storage */
  autoArchive: boolean;

  /** Archive logs after X days */
  archiveAfterDays: number;

  /** Deletion policy after retention */
  deletionPolicy: EchoDeletionPolicy;

  /** Destination for archived logs */
  archiveDestination?: EchoArchiveDestination;
}

// ═════════════════════════════════════════════════════════════
//  FAILURE HANDLING
// ═════════════════════════════════════════════════════════════

export type EchoFailureMode = "FAIL_REQUEST" | "CONTINUE_AND_ALERT";
export type EchoNormalMode = "BEST_EFFORT" | "GUARANTEED";

export interface EchoRetryConfig {
  maxAttempts: number;
  backoffMs: number;
}

export interface EchoFailedQueueConfig {
  enabled: boolean;
  maxSize: number;
  alertThreshold: number;
}

export interface EchoFileSpilloverConfig {
  enabled: boolean;
  path: string;
  maxSizeGB: number;
}

export interface EchoFailureHandling {
  /** Critical operations (user.delete, permission.grant) */
  critical: EchoFailureMode;

  /** Normal operations (chat.create, message.send) */
  normal: EchoNormalMode;

  /** Retry configuration */
  retries: EchoRetryConfig;

  /** Failed queue settings */
  failedQueue: EchoFailedQueueConfig;

  /** File spillover (last resort) */
  fileSpillover: EchoFileSpilloverConfig;
}

// ═════════════════════════════════════════════════════════════
//  PRIVACY & PII
// ═════════════════════════════════════════════════════════════

export type EchoRedactionStrategy = "mask" | "hash" | "remove";

export interface EchoPIIPatterns {
  email: boolean;
  phone: boolean;
  ssn: boolean;
  creditCard: boolean;
  custom: string[]; // Regex patterns
}

export interface EchoPrivacy {
  /** Automatic PII redaction */
  redactSensitiveFields: boolean;

  /** Field patterns to redact */
  sensitiveFieldPatterns: string[];

  /** Redaction strategy */
  redactionStrategy: EchoRedactionStrategy;

  /** PII pattern detection */
  piiPatterns: EchoPIIPatterns;

  /**
   * Drop the request IP from the audit event (lands as `null` on the row).
   * Most conservative GDPR/SOC 2 posture — auditor sees "no IP retained" with
   * zero ambiguity. Cleaner than masked-last-octet, which some readings still
   * treat as PII. Opt-in (default `false`) so existing customers keep the
   * current capture behaviour until they enable it.
   */
  anonymizeIP: boolean;

  /**
   * Resolve the request IP to country / region / city via the bundled
   * GeoLite2 data and write those into the audit row's geo columns.
   * Composes with `anonymizeIP`: the lookup runs BEFORE the IP is dropped,
   * so a customer can ship "country known, IP anonymous" — the strictest
   * defensible privacy posture for security analytics. Opt-in (`false`)
   * because geo capture is itself a privacy choice — some orgs prefer no
   * derived location data either.
   */
  trackGeolocation: boolean;
}

// ═════════════════════════════════════════════════════════════
//  EXPORT & DEPLOYMENT
// ═════════════════════════════════════════════════════════════

export type EchoExportFrequency = "hourly" | "daily" | "weekly" | "monthly";
export type EchoExportFormat = "json" | "csv" | "parquet";
export type EchoCompression = "none" | "gzip" | "zstd";

export interface EchoScheduledExport {
  enabled: boolean;
  frequency: EchoExportFrequency;
  time?: string; // "02:00" for daily/weekly
  dayOfWeek?: number; // 0-6 for weekly
  dayOfMonth?: number; // 1-31 for monthly
  destination: {
    type: EchoStorageType | "webhook";
    config: Record<string, unknown>;
  };
  format: EchoExportFormat;
  compression: EchoCompression;
}

export type EchoStreamingType = "webhook" | "kafka" | "kinesis" | "pubsub";

export interface EchoStreamingDestination {
  type: EchoStreamingType;
  url: string;
  filters?: {
    eventTypes?: string[];
    severity?: string[];
  };
  batchSize?: number;
  batchTimeoutMs?: number;
}

export interface EchoExportConfig {
  /** Manual exports (UI-triggered) */
  allowManual: boolean;

  /** Max records per export */
  maxRecordsPerExport: number;

  /** Scheduled exports */
  scheduled: EchoScheduledExport;

  /** Real-time streaming */
  streaming: {
    enabled: boolean;
    destinations: EchoStreamingDestination[];
  };
}

// ═════════════════════════════════════════════════════════════
//  ALERTS & NOTIFICATIONS
// ═════════════════════════════════════════════════════════════

export type EchoAlertSeverity = "warning" | "error" | "critical";

export interface EchoFailureAlert {
  enabled: boolean;
  threshold: number; // Alert after X failures
  cooldownMinutes: number; // Don't spam alerts
}

export interface EchoAnomalyRule {
  name: string;
  condition: string; // "event_rate > 1000/min"
  severity: EchoAlertSeverity;
}

export interface EchoAnomalyAlert {
  enabled: boolean;
  rules: EchoAnomalyRule[];
}

export interface EchoEmailChannel {
  enabled: boolean;
  recipients: string[];
}

export interface EchoSlackChannel {
  enabled: boolean;
  webhookUrl: string;
  channel?: string;
}

export interface EchoWebhookChannel {
  enabled: boolean;
  url: string;
  headers?: Record<string, string>;
}

export interface EchoPagerDutyChannel {
  enabled: boolean;
  deploymentKey: string;
}

export interface EchoAlertChannels {
  email?: EchoEmailChannel;
  slack?: EchoSlackChannel;
  webhook?: EchoWebhookChannel;
  pagerduty?: EchoPagerDutyChannel;
}

export interface EchoAlerts {
  enabled: boolean;

  /** Alert on failures */
  onFailure: EchoFailureAlert;

  /** Alert on anomalies */
  onAnomaly: EchoAnomalyAlert;

  /** Notification channels */
  channels: EchoAlertChannels;
}

// ═════════════════════════════════════════════════════════════
//  PERFORMANCE & OPTIMIZATION
// ═════════════════════════════════════════════════════════════

export type EchoQueuePriority = "low" | "normal" | "high";
export type EchoPartitionStrategy = "monthly" | "weekly" | "daily";

export interface EchoBatching {
  enabled: boolean;
  maxBatchSize: number;
  maxBatchTimeMs: number;
}

export interface EchoPerformance {
  /** Batching configuration */
  batching: EchoBatching;

  /** Async processing queue priority */
  queuePriority: EchoQueuePriority;

  /** Database partitioning */
  usePartitioning: boolean;

  /** Partition strategy */
  partitionStrategy: EchoPartitionStrategy;

  /** Custom indexes for specific queries */
  additionalIndexes: string[];
}

// ═════════════════════════════════════════════════════════════
//  COMPLIANCE & AUDIT
// ═════════════════════════════════════════════════════════════

export type EchoComplianceStandard = "SOC2" | "GDPR" | "HIPAA" | "PCI-DSS" | "ISO27001";
export type EchoSignatureAlgorithm = "HMAC-SHA256" | "RSA-SHA256";

export interface EchoSignatures {
  enabled: boolean;
  algorithm: EchoSignatureAlgorithm;
  secretKey?: string; // Or use KMS
}

export type EchoReportFrequency = "weekly" | "monthly" | "quarterly";

export interface EchoComplianceReports {
  enabled: boolean;
  frequency: EchoReportFrequency;
  recipients: string[];
}

export interface EchoCompliance {
  /** Compliance standards */
  standards: EchoComplianceStandard[];

  /** Immutability enforcement at DB level */
  immutable: boolean;

  /** Digital signatures */
  signatures: EchoSignatures;

  /** Meta-logging (log Echo system events) */
  metaLogging: boolean;

  /** Compliance reports */
  reports: EchoComplianceReports;
}

// ═════════════════════════════════════════════════════════════
//  ADVANCED FEATURES (Phase 7)
// ═════════════════════════════════════════════════════════════

export type EchoSIEMProvider = "splunk" | "datadog" | "elastic" | "custom";

export interface EchoSIEM {
  enabled: boolean;
  provider: EchoSIEMProvider;
  endpoint?: string;
  apiKey?: string;
}

export interface EchoAnalytics {
  enabled: boolean;
  dashboards: boolean;
  anomalyDetection: boolean;
  mlModels: string[];
}

export interface EchoQueryAPI {
  enabled: boolean;
  maxResultsPerQuery: number;
  rateLimitPerMin: number;
}

export interface EchoAdvanced {
  /** SIEM deployment */
  siem: EchoSIEM;

  /** Analytics */
  analytics: EchoAnalytics;

  /** Query API */
  queryApi: EchoQueryAPI;

  /**
   * Event-type patterns treated as critical when the @Echo() decorator on the
   * endpoint did not explicitly set `critical: true`. Critical events route
   * through `failureMode.critical` (typically FAIL_REQUEST) on audit failure;
   * non-critical events use `failureMode.normal` (typically BEST_EFFORT).
   *
   * Pattern grammar matches `event-pattern.matcher.ts`: `"*"` (catch-all),
   * `"<prefix>.*"` (dot-bounded prefix), or an exact event id. An empty array
   * means no events are treated as critical via the fallback path — the
   * decorator's `critical: true` override still wins.
   */
  criticalEventPatterns: string[];
}

// ═════════════════════════════════════════════════════════════
//  COMPLETE CONFIGURATION
// ═════════════════════════════════════════════════════════════

export interface OrganizationEchoConfig {
  /** Master switch */
  enabled: boolean;

  /** Config version (for migrations) */
  version: string;

  /** Event filtering */
  events: EchoEventFiltering;

  /** Retention & archival */
  retention: EchoRetention;

  /** Failure handling */
  failureMode: EchoFailureHandling;

  /** Privacy & PII */
  privacy: EchoPrivacy;

  /** Export & deployment */
  export: EchoExportConfig;

  /** Alerts & notifications */
  alerts: EchoAlerts;

  /** Performance & optimization */
  performance: EchoPerformance;

  /** Compliance & audit */
  compliance: EchoCompliance;

  /** Advanced features */
  advanced: EchoAdvanced;
}

// ═════════════════════════════════════════════════════════════
//  DEFAULT CONFIGURATION
// ═════════════════════════════════════════════════════════════

export const DEFAULT_ECHO_CONFIG: OrganizationEchoConfig = {
  enabled: false, // Opt-in
  version: "1.0.0",

  events: {
    enabled: ["*"], // Everything
    disabled: [],
    sampling: {}
  },

  retention: {
    daysInDatabase: 90,
    autoArchive: false,
    archiveAfterDays: 90,
    // EchoRetentionService deletes rows past `expiresAt`; there is no
    // archival step today, so the honest default is "after_retention".
    // `after_archive` remains in the enum for when archival lands.
    deletionPolicy: "after_retention"
  },

  failureMode: {
    critical: "FAIL_REQUEST",
    normal: "BEST_EFFORT",
    retries: {
      maxAttempts: 3,
      backoffMs: 1000
    },
    failedQueue: {
      enabled: true,
      maxSize: 100000,
      alertThreshold: 1000
    },
    fileSpillover: {
      enabled: true,
      path: "/var/log/echo/spillover",
      maxSizeGB: 10
    }
  },

  privacy: {
    redactSensitiveFields: false,
    sensitiveFieldPatterns: ["password", "token", "secret", "apiKey"],
    redactionStrategy: "mask",
    piiPatterns: {
      email: false,
      phone: false,
      ssn: false,
      creditCard: false,
      custom: []
    },
    anonymizeIP: false,
    trackGeolocation: false
  },

  export: {
    allowManual: true,
    maxRecordsPerExport: 1000000,
    scheduled: {
      enabled: false,
      frequency: "daily",
      format: "json",
      compression: "gzip",
      destination: {
        type: "s3",
        config: {}
      }
    },
    streaming: {
      enabled: false,
      destinations: []
    }
  },

  alerts: {
    enabled: true,
    onFailure: {
      enabled: true,
      threshold: 10,
      cooldownMinutes: 15
    },
    onAnomaly: {
      enabled: false,
      rules: []
    },
    channels: {}
  },

  performance: {
    batching: {
      enabled: true,
      maxBatchSize: 100,
      maxBatchTimeMs: 5000
    },
    queuePriority: "normal",
    usePartitioning: true,
    partitionStrategy: "monthly",
    additionalIndexes: []
  },

  compliance: {
    standards: ["SOC2"],
    immutable: true,
    signatures: {
      enabled: false,
      algorithm: "HMAC-SHA256"
    },
    metaLogging: true,
    reports: {
      enabled: false,
      frequency: "monthly",
      recipients: []
    }
  },

  advanced: {
    siem: {
      enabled: false,
      provider: "custom"
    },
    analytics: {
      enabled: false,
      dashboards: false,
      anomalyDetection: false,
      mlModels: []
    },
    queryApi: {
      enabled: true,
      maxResultsPerQuery: 10000,
      rateLimitPerMin: 100
    },
    // Default fallback list — when the @Echo() decorator does not set
    // `critical: true` explicitly, an event matching any of these patterns
    // is routed through the critical failure mode. Orgs may override.
    criticalEventPatterns: [
      "user.delete",
      "user.permission.*",
      "organization.delete",
      "integration.delete",
      "workflow.delete",
      "agent.delete"
    ]
  }
};
